import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Beer, Martini, Minus, Plus, Banknote, CreditCard, Loader2, CheckCircle2 } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { typeText } from "@/lib/ticketTypes";

const DRINKS = [
  { key: "BEER",     label: "BEER",     Icon: Beer,    price: 5  },
  { key: "COCKTAIL", label: "COCKTAIL", Icon: Martini, price: 10 },
];

const PAY_METHODS = [
  { key: "CASH",   label: "CASH",   Icon: Banknote,   tone: "emerald" },
  { key: "STRIPE", label: "CARD",   Icon: CreditCard, tone: "amber"   },
];

const toneClasses = (tone, active) => {
  const base = active ? `text-white scale-[1.02]` : "bg-slate-800 text-slate-200 hover:bg-slate-700";
  if (!active) return base;
  switch (tone) {
    case "emerald": return `bg-emerald-600 ${base}`;
    case "amber":   return `bg-gradient-to-br from-orange-500 to-amber-500 ${base}`;
    default:        return base;
  }
};

// Stripe loader cache so we don't rebuild the Stripe client on every open
let stripePromiseCache = null;
const getStripe = (pk) => {
  if (!pk) return null;
  if (!stripePromiseCache || stripePromiseCache._pk !== pk) {
    stripePromiseCache = loadStripe(pk);
    stripePromiseCache._pk = pk;
  }
  return stripePromiseCache;
};

function StripePayForm({ pending, onPaid, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // Poll backend for webhook-confirmed status
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/pending/${pending.reference}`);
        if (data.status === "paid") { clearInterval(pollRef.current); onPaid(data); }
        else if (data.status === "error" || data.status === "cancelled") { clearInterval(pollRef.current); setError(data.error || "Payment did not complete"); }
      } catch { /* keep polling */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [pending.reference, onPaid]);

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setError(null);
    const { error: err, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (err) {
      setError(err.message || "Payment failed");
      setBusy(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      // Webhook will finalise; poll will flip to paid shortly.
      toast.success("Payment captured · crediting drinks…");
    }
    setBusy(false);
  };

  return (
    <form onSubmit={handlePay} className="space-y-3" data-testid="stripe-pay-form">
      <div className="rounded-xl bg-[#0B1120] border border-amber-500/30 p-4">
        <PaymentElement options={{ layout: "tabs" }} />
      </div>
      {error && <div className="text-sm text-red-400" data-testid="stripe-error">{error}</div>}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1 bg-slate-800 border-slate-700 text-slate-200" data-testid="stripe-cancel">CANCEL</Button>
        <Button type="submit" disabled={!stripe || busy} className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold" data-testid="stripe-pay-btn">
          {busy ? <Loader2 className="animate-spin" size={16} /> : `PAY $${pending.total_amount}`}
        </Button>
      </div>
      <p className="text-[10px] text-slate-500">Test card: 4242 4242 4242 4242 · any future date · any CVC. Drinks are credited only after Stripe confirms the payment.</p>
    </form>
  );
}

export default function PurchaseDrinkModal({ open, onClose, ticket, onSaved }) {
  const [qtys, setQtys] = useState({ BEER: 0, COCKTAIL: 0 });
  const [pay, setPay] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [publishableKey, setPublishableKey] = useState("");

  // Stripe in-flight state
  const [pending, setPending] = useState(null); // {reference, client_secret, total_quantity, total_amount}
  const [pendingStatus, setPendingStatus] = useState("pending"); // pending|paid|cancelled|error

  useEffect(() => {
    if (open) {
      setQtys({ BEER: 0, COCKTAIL: 0 });
      setPay(null);
      setPending(null);
      setPendingStatus("pending");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    api.get("/payments/config").then((r) => {
      setStripeEnabled(!!r.data?.stripe_enabled);
      setPublishableKey(r.data?.publishable_key || "");
    }).catch(() => setStripeEnabled(false));
  }, [open]);

  const total = useMemo(() => qtys.BEER + qtys.COCKTAIL, [qtys]);
  const totalAmount = useMemo(() => qtys.BEER * 5 + qtys.COCKTAIL * 10, [qtys]);
  const canSubmit = useMemo(() => pay && total > 0, [pay, total]);

  const bump = (key, delta) => setQtys((q) => ({ ...q, [key]: Math.max(0, (q[key] || 0) + delta) }));

  const itemsPayload = () =>
    DRINKS.map((d) => ({ drink_type: d.key, quantity: qtys[d.key] })).filter((it) => it.quantity > 0);

  const submitManual = async () => {
    setBusy(true);
    try {
      await api.post(`/tickets/${ticket.id}/purchase`, { items: itemsPayload(), payment_method: "CASH" });
      toast.success(`Added ${total} drink${total > 1 ? "s" : ""} · $${totalAmount} cash`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/create-intent", { ticket_id: ticket.id, items: itemsPayload() });
      setPending(data);
      setPendingStatus("pending");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submit = () => {
    if (!pay) { toast.error("SELECT PAYMENT METHOD"); return; }
    if (total <= 0) { toast.error("Add at least one drink"); return; }
    return pay === "STRIPE" ? submitStripe() : submitManual();
  };

  const cancelPending = async () => {
    if (!pending) return;
    try { await api.post(`/payments/pending/${pending.reference}/cancel`); } catch { /* ignore */ }
    setPendingStatus("cancelled");
    setPending(null);
  };

  const onStripePaid = () => {
    setPendingStatus("paid");
    toast.success(`Payment confirmed · +${pending.total_quantity} drink${pending.total_quantity > 1 ? "s" : ""}`);
    setTimeout(() => { onSaved?.(); }, 900);
  };

  if (!ticket) return null;

  const stripePromise = pending && publishableKey ? getStripe(publishableKey) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !pending && onClose?.()}>
      <DialogContent className="bg-[#131D33] border-slate-700 text-white max-w-lg" data-testid="purchase-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">ADD ADDITIONAL DRINKS</DialogTitle>
        </DialogHeader>

        <div className="rounded-xl bg-[#0B1120] border border-slate-800 p-4 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Ticket</span><span className="font-mono-fb text-orange-300">{ticket.ticket_id}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Type</span><span className={typeText(ticket.ticket_type)}>{ticket.ticket_type}</span></div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-center">
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Included left</div><div className="font-display text-xl font-black">{ticket.base_drinks_remaining}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-orange-400">Credit</div><div className="font-display text-xl font-black text-orange-300" data-testid="purchase-current-credit">${((ticket.credit_cents || 0) / 100).toFixed(2)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Drinks left</div><div className="font-display text-xl font-black text-teal-300">{ticket.drinks_remaining}</div></div>
          </div>
        </div>

        {!pending && (
          <>
            <div>
              <div className="text-[10px] tracking-widest uppercase text-orange-400 font-bold mb-2">SELECT ADDITIONAL DRINKS</div>
              <div className="space-y-2">
                {DRINKS.map((d) => (
                  <div key={d.key} className="flex items-center justify-between rounded-xl bg-slate-900/70 border border-orange-500/20 p-3">
                    <div className="flex items-center gap-3">
                      <d.Icon size={22} className="text-orange-300" />
                      <div>
                        <div className="font-display text-lg font-bold tracking-tight">{d.label}</div>
                        <div className="text-[11px] text-slate-400">${d.price} each</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" onClick={() => bump(d.key, -1)} className="bg-slate-800 border-slate-700 h-10 w-10 p-0" data-testid={`qty-minus-${d.key.toLowerCase()}`}><Minus size={14} /></Button>
                      <div className="font-display text-2xl font-black w-10 text-center" data-testid={`qty-value-${d.key.toLowerCase()}`}>{qtys[d.key]}</div>
                      <Button variant="outline" onClick={() => bump(d.key, 1)} className="bg-slate-800 border-orange-500/40 text-orange-300 h-10 w-10 p-0" data-testid={`qty-plus-${d.key.toLowerCase()}`}><Plus size={14} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-widest uppercase text-slate-400 font-bold mb-2">PAYMENT METHOD</div>
              <div className="grid grid-cols-2 gap-2">
                {PAY_METHODS.map((m) => {
                  if (m.key === "STRIPE" && !stripeEnabled) return null;
                  const active = pay === m.key;
                  return (
                    <button key={m.key} onClick={() => setPay(m.key)} data-testid={`pay-${m.key.toLowerCase()}`}
                      className={`rounded-xl p-4 flex items-center justify-center gap-2 font-display font-black tracking-tight ${toneClasses(m.tone, active)}`}>
                      <m.Icon size={18} /> {m.label}
                    </button>
                  );
                })}
              </div>
              {pay === "STRIPE" && (
                <p className="mt-2 text-[11px] text-amber-300/80">Card is captured right here. Drinks are credited only after Stripe confirms the payment.</p>
              )}
            </div>

            {(pay || total > 0) && (
              <div className="rounded-xl bg-[#0B1120] border border-slate-800 p-4 text-sm">
                <div className="text-[10px] tracking-widest uppercase text-slate-400 font-bold mb-2">Transaction Summary</div>
                {DRINKS.filter((d) => qtys[d.key] > 0).map((d) => (
                  <div key={d.key} className="flex justify-between"><span>{d.label} <span className="text-slate-500">(${d.price})</span></span><b>× {qtys[d.key]} = ${d.price * qtys[d.key]}</b></div>
                ))}
                {total === 0 && <div className="text-slate-500 text-xs italic">Add at least one drink</div>}
                <div className="flex justify-between mt-2"><span>Payment</span><b>{pay === "STRIPE" ? "CARD" : (pay || "—")}</b></div>
                <div className="flex justify-between"><span>Current Credit</span><b>${((ticket.credit_cents || 0) / 100).toFixed(2)}</b></div>
                <div className="flex justify-between border-t border-slate-800 mt-2 pt-2"><span>Total Amount (credit added)</span><b className="text-amber-300 text-lg">${totalAmount}</b></div>
                <div className="flex justify-between"><span>New Credit</span><b className="text-teal-300 text-lg" data-testid="purchase-new-credit">${((ticket.credit_cents || 0) / 100 + Number(totalAmount)).toFixed(2)}</b></div>
              </div>
            )}

            <div className="flex gap-2 mt-2">
              <Button variant="outline" onClick={onClose} className="flex-1 bg-slate-800 border-slate-700 text-slate-200" data-testid="purchase-cancel">CANCEL</Button>
              <Button onClick={submit} disabled={!canSubmit || busy}
                className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:brightness-110 text-white font-bold"
                data-testid="purchase-confirm">
                {pay === "STRIPE" ? "CONTINUE TO CARD" : "CONFIRM & ADD DRINKS"}
              </Button>
            </div>
          </>
        )}

        {pending && (
          <div className="rounded-xl bg-[#0B1120] border border-amber-500/30 p-4 space-y-3" data-testid="stripe-pending">
            {pendingStatus === "pending" && stripePromise && (
              <Elements stripe={stripePromise} options={{
                clientSecret: pending.client_secret,
                appearance: {
                  theme: "night",
                  variables: { colorPrimary: "#f59e0b", colorBackground: "#0B1120", colorText: "#f1f5f9", borderRadius: "10px" },
                },
              }}>
                <div className="flex items-center justify-between text-xs text-slate-300">
                  <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Reference</div><div className="font-mono text-orange-300">{pending.reference}</div></div>
                  <div><div className="text-[10px] uppercase tracking-widest text-slate-500">Drinks</div><b>+{pending.total_quantity} · ${pending.total_amount}</b></div>
                </div>
                <StripePayForm pending={pending} onPaid={onStripePaid} onCancel={cancelPending} />
              </Elements>
            )}
            {pendingStatus === "paid" && (
              <div className="flex items-center gap-2 text-emerald-300 font-bold" data-testid="stripe-paid">
                <CheckCircle2 size={20} /> Payment confirmed · +{pending.total_quantity} drinks added
              </div>
            )}
            {pendingStatus === "cancelled" && (
              <div className="text-slate-400 text-sm" data-testid="stripe-cancelled">
                Payment cancelled.
                <Button variant="outline" onClick={onClose} className="mt-3 w-full bg-slate-800 border-slate-700 text-slate-200">Close</Button>
              </div>
            )}
            {pendingStatus === "error" && (
              <div className="text-red-400 text-sm">
                Something went wrong finalising the payment. Please check Admin → Activity Log.
                <Button variant="outline" onClick={onClose} className="mt-3 w-full bg-slate-800 border-slate-700 text-slate-200">Close</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
